import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { colorFor } from "./palette";

const TARGET_LABEL = "Target";
const TARGET_COLOR = "#cfa77b"; // Rare gold, so the target never reads as a band

/**
 * New business pipeline by confidence band, against target.
 *
 * The target is drawn as its own bar, not only as a line. A £455k line across a
 * £6.8m bar sits flat on the axis and reads as a border — the comparison the
 * board actually wants is target beside total, so the bar carries it and the
 * line stays as a reading aid across the bands.
 *
 * Bands are coloured on one scale rather than from the categorical palette,
 * because they are ordered — 10% through 100% is a progression, and giving
 * each its own unrelated colour implies they are unrelated categories.
 */
// Low to high confidence, light mint to Rare teal-deep and navy.
const BAND_COLORS = ["#c8f4ea", "#a7f0df", "#7ee8d0", "#50e3c2", "#2fb8a4", "#119996", "#1f5d74"];

function bandColor(index, total) {
  if (total <= 1) return BAND_COLORS[BAND_COLORS.length - 1];
  const position = Math.round((index / (total - 1)) * (BAND_COLORS.length - 1));
  return BAND_COLORS[position];
}

export default function BarTargetChart({
  groups = [],
  target,
  targetLabel = TARGET_LABEL,
  colors = {},
  formatValue,
  targetAsBar = false,
  onGroupClick,
}) {
  const format = formatValue || ((v) => new Intl.NumberFormat("en-GB").format(v));
  const compact = (value) =>
    new Intl.NumberFormat("en-GB", {
      notation: "compact",
      maximumFractionDigits: 1,
    }).format(value);

  const bands = groups.map((group, index) => ({
    name: group.label,
    value: group.value,
    fill: Object.keys(colors).length
      ? colorFor(group.label, index, colors)
      : bandColor(index, groups.length),
    isTarget: false,
  }));

  if (!bands.length) {
    return <p className="py-12 text-center text-sm text-[#4a5b68]">No records match this report.</p>;
  }

  const hasTarget = typeof target === "number";

  // The target as its own bar, first, then the bands. A £455k line across a
  // £6.8m bar sits on the axis and reads as a border; as a bar it is something
  // to compare against.
  const data =
    hasTarget && targetAsBar
      ? [{ name: targetLabel, value: target, fill: TARGET_COLOR, isTarget: true }, ...bands]
      : bands;

  return (
    <ResponsiveContainer width="100%" height={340} minWidth={280}>
      <BarChart data={data} margin={{ top: 26, right: 16, left: 4, bottom: 0 }}>
        <CartesianGrid stroke="#dddddd" vertical={false} />
        <XAxis
          dataKey="name"
          tickLine={false}
          axisLine={false}
          interval={0}
          tick={{ fill: "#4a5b68", fontSize: 11 }}
        />
        <YAxis
          tickLine={false}
          axisLine={false}
          tick={{ fill: "#4a5b68", fontSize: 11 }}
          tickFormatter={compact}
        />
        <Tooltip
          cursor={{ fill: "rgba(15, 23, 42, 0.04)" }}
          contentStyle={{ borderRadius: 4, borderColor: "#cccccc", boxShadow: "0 4px 12px rgba(51, 71, 91, 0.12)" }}
          formatter={(value) => [format(value), "Amount"]}
        />
        <Bar
          dataKey="value"
          maxBarSize={64}
          radius={[2, 2, 0, 0]}
          isAnimationActive={false}
          cursor={onGroupClick ? "pointer" : undefined}
          onClick={(entry) => {
            const name = entry?.name ?? entry?.payload?.name;
            // The target bar is a reference, not a group to open.
            if (onGroupClick && name && name !== targetLabel) {
              onGroupClick(name);
            }
          }}
        >
          {data.map((entry) => (
            <Cell key={entry.name} fill={entry.fill} />
          ))}
          {/* Compact labels: seven full currency strings across one axis
              overlap into an unreadable band. */}
          <LabelList dataKey="value" position="top" fill="#323c44" fontSize={11} formatter={compact} />
        </Bar>
        {hasTarget && (
          <ReferenceLine
            y={target}
            stroke={TARGET_COLOR}
            strokeDasharray="5 4"
            strokeWidth={1.25}
            ifOverflow="extendDomain"
          />
        )}
      </BarChart>
    </ResponsiveContainer>
  );
}
